import React, { useMemo, FC } from 'react';
import { Column, Paragraph, Row, Separator, Alert, Loader } from '@wunder-mobility/ui-components';
import { useFormikContext } from 'formik';
import { BookingOverviewForm } from '@/features/booking/types';
import { VehicleCategory } from '@/features/vehicles/types';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import AdditionPriceBreakdown from '@/features/booking/components/BookingFlow/components/AdditionsSelection/AdditionPriceBreakdown';
import { useRequestBookingSettingQuery } from '@/core/services/SettingsManagement.service';
import CurrencyDisplay from '@/core/components/CurrencyDisplay';
import UnitDisplay from '@/core/components/UnitDisplay';
import { useLingui } from '@lingui/react';
import { getNormalizedUnit } from '@/core/utils/getNormalizedUnit';
import styles from '@/features/booking/assets/scss/components/PricingBreakdown.module.scss';
import {
  useRequestBookingEstimatedPricingQuery,
  useRequestBookingByIdQuery,
} from '@/core/features/booking/services/Booking.service';
import { mergeDateTime } from '@/core/features/booking/utils/mergeDateTime';
import { useTypedSelector } from '@/core/redux/hooks';
import { getVehicleCategoryAdditions } from '@/features/vehicles/redux/Vehicles.selector';

interface PricingBreakdownT {
  vehicleCategory: VehicleCategory;
  branchId: string;
  bookingId?: string;
}

const PricingBreakdown: FC<PricingBreakdownT> = ({ vehicleCategory, branchId, bookingId }) => {
  const { values } = useFormikContext<BookingOverviewForm>();
  const additionsData = useTypedSelector(getVehicleCategoryAdditions);
  const { data: bookingSettings } = useRequestBookingSettingQuery();
  const { showGrossPrice } = bookingSettings?.value ?? {};
  const { i18n } = useLingui();

  const { data: originalBooking, isFetching: isFetchingOriginalBooking } =
    useRequestBookingByIdQuery({ id: Number(bookingId) }, { skip: !bookingId });

  const { data: originalEstimatedPrice, isFetching: isFetchingOriginalPriceEstimation } =
    useRequestBookingEstimatedPricingQuery(
      {
        branchId: +branchId,
        startTime: originalBooking?.startTime ?? '',
        endTime: originalBooking?.endTime ?? '',
        additions: originalBooking?.additions ?? [],
        vehicleCategoryId: originalBooking?.vehicleCategoryId ?? 0,
        userGroupCode: originalBooking?.userGroupCode,
      },
      { skip: !bookingId || !originalBooking },
    );

  const {
    data: estimatedPrice,
    isFetching: isFetchingPriceEstimation,
    error: isEstimatedPriceError,
  } = useRequestBookingEstimatedPricingQuery({
    branchId: +branchId,
    startTime: mergeDateTime(values.bookingDate!.from!, values.bookingTime?.from).toISOString(),
    endTime: mergeDateTime(
      values.bookingDate!.to ?? values.bookingDate!.from!,
      values.bookingTime?.to,
    ).toISOString(),
    additions:
      values.selectedAdditions?.map((addition) => addition) ?? originalBooking?.additions ?? [],
    vehicleCategoryId: values.selectedVehicleCategory!,
    userGroupCode:
      values.selectedPaymentType === 'BUSINESS' ? values.selectedPaymentMethod! : undefined,
    bookingId,
  });

  const totalPrice = useMemo(() => {
    if (!vehicleCategory || !estimatedPrice) return { net: 0, gross: 0 };

    return {
      gross: estimatedPrice.grossPrice,
      net: estimatedPrice.netPrice,
    };
  }, [vehicleCategory, estimatedPrice]);

  const priceComparison = useMemo(() => {
    if (!bookingId || !originalBooking || !estimatedPrice) return null;

    const original =
      (showGrossPrice ? originalEstimatedPrice?.grossPrice : originalEstimatedPrice?.netPrice) ?? 0;

    const updated = (showGrossPrice ? estimatedPrice.grossPrice : estimatedPrice.netPrice) ?? 0;

    return {
      original,
      updated,
      difference: updated - original,
      currency: estimatedPrice.currency,
    };
  }, [bookingId, originalBooking, estimatedPrice, originalEstimatedPrice, showGrossPrice]);

  const isEditing = !!bookingId && !!priceComparison;

  if (!vehicleCategory || isEstimatedPriceError) return null;

  if (isFetchingPriceEstimation || isFetchingOriginalBooking || isFetchingOriginalPriceEstimation)
    return <Loader />;

  return (
    <>
      {isEditing && priceComparison && (
        <Row justify="space-between" marginBottom={12}>
          <Column>
            <Paragraph marginBottom={0} weight="bold">
              <Trans id="bookings.createSummary.originalPrice">Original price</Trans>
              {showGrossPrice && (
                <span className={styles['pricing-breakdown__tax']}>
                  {' '}
                  (<Trans id="general.includeTaxShort">Incl. VAT</Trans>)
                </span>
              )}
            </Paragraph>
          </Column>
          <Column>
            <Paragraph marginBottom={0} weight="bold">
              <CurrencyDisplay
                value={priceComparison.original}
                currency={priceComparison.currency}
              />
            </Paragraph>
          </Column>
        </Row>
      )}
      <Row justify="space-between">
        <Column>
          <Paragraph marginBottom={0} weight="medium">
            <Trans id="bookings.createSummary.rentalPrice">Rental price</Trans>
          </Paragraph>
        </Column>
        <Column>
          <Paragraph marginBottom={0} weight="medium">
            <CurrencyDisplay
              value={
                showGrossPrice
                  ? estimatedPrice!.pricing.timePrice!.timeTotalGross
                  : estimatedPrice!.pricing.timePrice!.timeTotalNet
              }
              currency={estimatedPrice!.currency}
            />
          </Paragraph>
        </Column>
      </Row>
      {estimatedPrice!.additions.map((estimationAddition) => {
        const fullAddition = additionsData?.find((add) => add.code === estimationAddition.code);
        const estimatedTotal = estimationAddition.price;

        // If addition exists in additionsData, use full addition data with price breakdown
        if (fullAddition) {
          return (
            <Row key={fullAddition.code} justify="space-between">
              <Column>
                <Paragraph marginBottom={0} weight="medium">
                  {fullAddition.name}
                </Paragraph>
              </Column>
              <Column>
                <Paragraph marginBottom={0} weight="medium">
                  <AdditionPriceBreakdown
                    addition={fullAddition}
                    currency={estimatedPrice!.currency}
                  />
                  <CurrencyDisplay value={estimatedTotal} currency={estimatedPrice!.currency} />
                </Paragraph>
              </Column>
            </Row>
          );
        }

        // Otherwise, render directly from estimation response (e.g., entitled additions)
        return (
          <Row key={estimationAddition.code} justify="space-between">
            <Column>
              <Paragraph marginBottom={0} weight="medium">
                {estimationAddition.name || estimationAddition.code}
              </Paragraph>
            </Column>
            <Column>
              <Paragraph marginBottom={0} weight="medium">
                <CurrencyDisplay value={estimatedTotal} currency={estimatedPrice!.currency} />
              </Paragraph>
            </Column>
          </Row>
        );
      })}
      {!showGrossPrice && (
        <>
          <Separator gapBottom={16} gapTop={8} />
          <Row justify="space-between" marginBottom={16}>
            <Column>
              <Paragraph marginBottom={0} weight="medium">
                <Trans id="bookings.createSummary.subTotal">Subtotal</Trans>
              </Paragraph>
            </Column>
            <Column>
              <Paragraph marginBottom={0} weight="medium">
                <CurrencyDisplay value={totalPrice.net} currency={estimatedPrice!.currency} />
              </Paragraph>
            </Column>
          </Row>

          <Row justify="space-between" marginBottom={16}>
            <Column>
              <Paragraph size={2} marginBottom={0} weight="medium">
                <Trans id="bookings.createSummary.VAT">VAT</Trans>
              </Paragraph>
            </Column>
            <Column>
              <Paragraph marginBottom={0} weight="medium">
                <CurrencyDisplay
                  value={totalPrice.gross - totalPrice.net}
                  currency={estimatedPrice!.currency}
                />
              </Paragraph>
            </Column>
          </Row>
        </>
      )}
      <Separator gapBottom={16} gapTop={8} />
      {isEditing && priceComparison ? (
        <>
          <Row justify="space-between" marginBottom={12}>
            <Column>
              <Paragraph marginBottom={0} weight="medium">
                <Trans id="bookings.createSummary.priceDifference">Price difference</Trans>
                {showGrossPrice && (
                  <span className={styles['pricing-breakdown__tax']}>
                    {' '}
                    (<Trans id="general.includeTaxShort">Incl. VAT</Trans>)
                  </span>
                )}
              </Paragraph>
            </Column>
            <Column>
              <Paragraph marginBottom={0} weight="medium">
                {priceComparison.difference >= 0 ? '+' : ''}
                <CurrencyDisplay
                  value={priceComparison.difference}
                  currency={priceComparison.currency}
                />
              </Paragraph>
            </Column>
          </Row>
          <Row justify="space-between" marginBottom={16}>
            <Column>
              <Paragraph marginBottom={0} weight="bold">
                <Trans id="bookings.createSummary.updatedPrice">Updated price</Trans>
                {showGrossPrice && (
                  <span className={styles['pricing-breakdown__tax']}>
                    {' '}
                    (<Trans id="general.includeTaxShort">Incl. VAT</Trans>)
                  </span>
                )}
              </Paragraph>
            </Column>
            <Column>
              <Paragraph marginBottom={0} weight="bold">
                <CurrencyDisplay
                  value={priceComparison.updated}
                  currency={priceComparison.currency}
                />
              </Paragraph>
            </Column>
          </Row>
        </>
      ) : (
        <Row justify="space-between" marginBottom={16}>
          <Column>
            <Paragraph size={2} marginBottom={0} weight="bold">
              <Trans id="bookings.createSummary.totalPrice">Total price</Trans>
              {showGrossPrice && (
                <span className={styles['pricing-breakdown__tax']}>
                  (<Trans id="general.includeTaxShort">Incl. VAT</Trans>)
                </span>
              )}
            </Paragraph>
          </Column>
          <Column>
            <Paragraph marginBottom={0} weight="bold">
              <CurrencyDisplay value={totalPrice.gross} currency={estimatedPrice!.currency} />
            </Paragraph>
          </Column>
        </Row>
      )}
      {!!estimatedPrice!.pricing?.distancePrice?.rate?.rate && (
        <Alert
          type="info"
          canBeClosed={false}
          className={styles['pricing-breakdown__distance']}
          icon="info"
          title={t({ id: 'bookings.createSummary.distancePrice', message: 'Distance price' })}
          description={
            <>
              {estimatedPrice!.pricing?.distancePrice?.rate?.included ? (
                <Trans id="bookings.createSummary.distancePriceTooltip">
                  After the included distance of{' '}
                  {i18n.number(estimatedPrice!.pricing.distancePrice.rate.included, {
                    unit: getNormalizedUnit(estimatedPrice!.pricing.distancePrice.unit),
                    style: 'unit',
                    unitDisplay: 'short',
                  })}
                  , a rate of{' '}
                  <CurrencyDisplay
                    value={estimatedPrice!.pricing?.distancePrice?.rate?.rate}
                    currency={estimatedPrice!.currency}
                  />
                  <UnitDisplay
                    unit={estimatedPrice!.pricing?.distancePrice?.unit}
                    unitDisplay="shortPer"
                  />{' '}
                  applies.
                </Trans>
              ) : (
                <>
                  <CurrencyDisplay
                    value={estimatedPrice!.pricing?.distancePrice?.rate?.rate}
                    currency={estimatedPrice!.currency}
                  />
                  <UnitDisplay
                    unit={estimatedPrice!.pricing?.distancePrice?.unit}
                    unitDisplay="shortPer"
                  />
                </>
              )}
            </>
          }
        />
      )}
    </>
  );
};

export default PricingBreakdown;
